import { FC, type MouseEvent } from 'react';
import { createImageBlockConfig } from '@blocknote/core';
import {
  ImagePreview,
  ResizableFileBlockWrapper,
  type ReactCustomBlockRenderProps,
} from '@blocknote/react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import FormatAlignCenter from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeft from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRight from '@mui/icons-material/FormatAlignRight';
import InsertPhoto from '@mui/icons-material/InsertPhoto';
import { Row } from '../../Layout';
import { ImageTextAlignment } from '../types';

type ImageRenderProps = ReactCustomBlockRenderProps<
  typeof createImageBlockConfig
>;

const alignBarClass = 'image-align-bar';

const ImageBlockRoot = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: '100%',
  '& [data-file-block]': {
    maxWidth: '100%',
  },
  '& .bn-file-block-content-wrapper, & .bn-visual-media-wrapper': {
    maxWidth: '100%',
  },
  '& img': {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
  },
  [`& .${alignBarClass}`]: {
    opacity: 0,
    pointerEvents: 'none',
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  [`&:hover .${alignBarClass}, &:focus-within .${alignBarClass}`]: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  '@media (hover: none)': {
    [`& .${alignBarClass}`]: {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
}));

const AlignBar = styled(Row)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(theme.spacingSizes.xxs),
  right: theme.spacing(theme.spacingSizes.xxs),
  zIndex: 1,
  justifyContent: 'flex-end',
  gap: '2px',
  padding: theme.spacing(0.25),
  borderRadius: 4,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
}));

const AlignIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  padding: theme.spacing(theme.spacingSizes.xxs),
  borderRadius: 4,
  backgroundColor: active ? theme.palette.grey[200] : 'transparent',
  '&:hover': {
    backgroundColor: active ? theme.palette.grey[300] : 'transparent',
  },
}));

const ImageBlock: FC<ImageRenderProps> = (props) => {
  const { block, editor } = props;
  const alignment = block.props.textAlignment;

  const setAlignment = (textAlignment: ImageTextAlignment) => {
    const current = editor.getBlock(block.id);
    if (!current || current.type !== 'image') {
      return;
    }
    editor.updateBlock(current, {
      props: {
        ...current.props,
        textAlignment,
      },
    });
    editor.focus();
  };

  const stopEditorTakingSelection = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <ImageBlockRoot contentEditable={false} suppressContentEditableWarning>
      {/* BlockNote types `ResizableFileBlockWrapper` for `file` only; image blocks use the same runtime contract. */}
      {/* @ts-expect-error — image render props are compatible with the file resizer wrapper */}
      <ResizableFileBlockWrapper
        {...props}
        buttonIcon={<InsertPhoto fontSize="large" />}
      >
        <ImagePreview {...props} />
      </ResizableFileBlockWrapper>
      {editor.isEditable ? (
        <AlignBar className={alignBarClass} centered>
          <AlignIconButton
            size="small"
            active={alignment === ImageTextAlignment.Left}
            title="Align image left"
            onMouseDown={stopEditorTakingSelection}
            onClick={() => {
              setAlignment(ImageTextAlignment.Left);
            }}
          >
            <FormatAlignLeft fontSize="small" />
          </AlignIconButton>
          <AlignIconButton
            size="small"
            active={alignment === ImageTextAlignment.Center}
            title="Align image centre"
            onMouseDown={stopEditorTakingSelection}
            onClick={() => {
              setAlignment(ImageTextAlignment.Center);
            }}
          >
            <FormatAlignCenter fontSize="small" />
          </AlignIconButton>
          <AlignIconButton
            size="small"
            active={alignment === ImageTextAlignment.Right}
            title="Align image right"
            onMouseDown={stopEditorTakingSelection}
            onClick={() => {
              setAlignment(ImageTextAlignment.Right);
            }}
          >
            <FormatAlignRight fontSize="small" />
          </AlignIconButton>
        </AlignBar>
      ) : null}
    </ImageBlockRoot>
  );
};

export default ImageBlock;
